import React, { useEffect, useMemo, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Button from "../../Button/Button";
import "./style.css";
import * as api from "../../api";
import LoadingSpinner from "../../storybook/LoadingSpinner";
import { useDispatch } from "react-redux";
import { cancelRepairReferenceReasons } from "../../ducks/redux/actions/userChoices";
import {
  Asset,
  AssetType,
  Statuses,
  UpdateRepair,
} from "../../models/Repair/Statuses";

interface CancelRepairProps extends RouteComponentProps<any> {}

type FollowUpOption = { id: string; value: string };

type BlockingRule = {
  dependsOn: Array<{ id: string; responseId: string }>;
  errorCode: string;
};

type ValidationRule = {
  type: string;
  errorCode: string;
  metadata?: { key: string; value: string }[];
};

type FollowUp = {
  questionId: string;
  label: string;
  sequence: string;
  type: "Text" | "Radio";
  options?: FollowUpOption[];
  blocking?: BlockingRule | null;
  isEnabled?: boolean;
  validationRules?: ValidationRule[];
};

type Reason = {
  id: string;
  optionValue: string;
  optionCode: string;
  isEnabled: boolean;
  additionalInfo?: FollowUp[];
};

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

const toBooleanEnabled = (value: any): boolean => {
  if (value === undefined || value === null) return true;
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    if (v === "true") return true;
    if (v === "false") return false;
  }
  return Boolean(value);
};

const CancelRepair: React.FC<CancelRepairProps> = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const workOrderRepair = useSelector(
    (state: any) => state?.addresses?.workOrderRepair
  );

  const cancellationReasonsRefJson = useSelector(
    (state: any) => state.userChoicesReducer?.cancellationReasonsRefJson
  );

  const woCancellationTimeoutSeconds = useSelector(
    (state: any) => state?.userChoicesReducer?.woCancellationTimeoutSeconds
  );
  const woCancellationPollingDelaySeconds = useSelector(
    (state: any) => state?.userChoicesReducer?.woCancellationPollingDelaySeconds
  );

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [reasons, setReasons] = useState<Reason[]>([]);
  const [selectedReasonId, setSelectedReasonId] = useState<string>("");
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [textValidationErrors, setTextValidationErrors] = useState<
    Record<string, string[]>
  >({});
  const [isCancelling, setIsCancelling] = useState(false);
  const [showCancelError, setShowCancelError] = useState(false);

  useEffect(() => {
    let mounted = true;

    if (!workOrderRepair) {
      props.history.push("/LandingView");
      return;
    }

    const mapCancellationReasons = (
      cancellationreasons: Record<string, any>
    ): Reason[] =>
      Object.keys(cancellationreasons)
        .map((k) => cancellationreasons[k])
        .map(
          (r: any): Reason => ({
            id: String(r?.id ?? ""),
            optionValue: String(r?.optionValue ?? ""),
            optionCode: String(r?.optionCode ?? ""),
            isEnabled: toBooleanEnabled(r?.isEnabled),
            additionalInfo: Array.isArray(r?.additionalInfo)
              ? r.additionalInfo.map(
                  (q: any): FollowUp => ({
                    questionId: String(q?.questionId ?? ""),
                    label: String(q?.label ?? ""),
                    sequence: String(q?.sequence ?? "0"),
                    type: q?.type === "Radio" ? "Radio" : "Text",
                    options: Array.isArray(q?.options)
                      ? q.options.map((o: any) => ({
                          id: String(o?.id ?? ""),
                          value: String(o?.value ?? ""),
                        }))
                      : undefined,
                    blocking:
                      q?.blocking && Array.isArray(q?.blocking?.dependsOn)
                        ? {
                            dependsOn: q.blocking.dependsOn.map((d: any) => ({
                              id: String(d?.id ?? ""),
                              responseId: String(d?.responseId ?? ""),
                            })),
                            errorCode: String(q.blocking?.errorCode ?? ""),
                          }
                        : null,
                    isEnabled:
                      q?.isEnabled !== undefined
                        ? toBooleanEnabled(q?.isEnabled)
                        : true,
                    validationRules: Array.isArray(q?.validationRules)
                      ? q.validationRules.map(
                          (vr: any): ValidationRule => ({
                            type: String(vr?.type ?? ""),
                            errorCode: String(vr?.errorCode ?? ""),
                            metadata: Array.isArray(vr?.metadata)
                              ? vr.metadata.map((m: any) => ({
                                  key: String(m?.key ?? ""),
                                  value: String(m?.value ?? ""),
                                }))
                              : [],
                          })
                        )
                      : [],
                  })
                )
              : [],
          })
        )
        .filter((r: Reason) => r.isEnabled)
        .sort((a, b) => Number(a.id) - Number(b.id));

    const setMappedReasons = (src: Record<string, any>) => {
      const mapped = mapCancellationReasons(src);
      if (mounted) {
        setReasons(mapped);
      }
    };

    const load = async () => {
      try {
        setIsLoading(true);

        if (
          cancellationReasonsRefJson &&
          Object.keys(cancellationReasonsRefJson).length > 0
        ) {
          setMappedReasons(cancellationReasonsRefJson as Record<string, any>);
          return;
        }

        const namespacename = "repair-cancellation-reasons";
        const resp = await api.getReferenceDataRepair(namespacename);
        const cancellationreasons = (resp?.[0]?.namespace?.keyValues ??
          {}) as unknown as Record<string, any>;
        dispatch(cancelRepairReferenceReasons(cancellationreasons));

        setMappedReasons(cancellationreasons);
      } catch (e) {
        if (mounted) setReasons([]);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    load();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workOrderRepair, props.history, cancellationReasonsRefJson]);

  useEffect(() => {
    if (showCancelError) {
      const el = document.getElementById("cancel-failed-alert");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [showCancelError]);

  const selectedReason = useMemo(
    () => reasons.find((r) => r.id === selectedReasonId),
    [reasons, selectedReasonId]
  );

  const blockingState = useMemo(() => {
    if (!selectedReason) return { code: "", qid: "" };
    const infos = (selectedReason.additionalInfo || []).filter(
      (q) => q?.isEnabled !== false
    );
    for (const q of infos) {
      if (!q.blocking || !Array.isArray(q.blocking.dependsOn)) continue;
      const match = q.blocking.dependsOn.some((d) => {
        const val = (answers[d.id] || "").trim();
        return val === String(d.responseId);
      });
      if (match) return { code: q.blocking.errorCode, qid: q.questionId };
    }
    return { code: "", qid: "" };
  }, [selectedReason, answers]);

  const activeBlockingErrorCode = blockingState.code;

  const hasUnansweredBlockingQuestion = useMemo(() => {
    if (!selectedReason) return false;
    const infos = (selectedReason.additionalInfo || []).filter(
      (q) => q?.isEnabled !== false
    );
    return infos.some((q) => {
      if (!q.blocking || !Array.isArray(q.blocking.dependsOn)) return false;
      if (q.type === "Radio") return false;
      const ans = (answers[q.questionId] || "").trim();
      return ans === "";
    });
  }, [selectedReason, answers]);

  const hasEmptyMandatoryText = useMemo(() => {
    if (!selectedReason) return false;
    const infos = (selectedReason.additionalInfo || []).filter(
      (q) => q?.isEnabled !== false
    );
    return infos.some((q) => {
      if (q.type !== "Text") return false;
      const rules = q.validationRules || [];
      const hasMandatoryRule = rules.some((r) => r.type === "mandatory");
      if (!hasMandatoryRule) return false;
      const ans = (answers[q.questionId] || "").trim();
      return ans === "";
    });
  }, [selectedReason, answers]);

  const hasUnansweredRadio = useMemo(() => {
    if (!selectedReason) return false;
    const infos = (selectedReason.additionalInfo || []).filter(
      (q) => q?.isEnabled !== false
    );
    return infos.some((q) => {
      if (q.type !== "Radio") return false;
      const ans = (answers[q.questionId] || "").trim();
      return ans === "";
    });
  }, [selectedReason, answers]);

  const canSubmitCancel = useMemo(() => {
    if (!selectedReasonId) return false;
    if (hasUnansweredBlockingQuestion) return false;
    if (activeBlockingErrorCode) return false;
    if (hasEmptyMandatoryText) return false;
    if (hasUnansweredRadio) return false;
    return true;
  }, [
    selectedReasonId,
    hasUnansweredBlockingQuestion,
    activeBlockingErrorCode,
    hasEmptyMandatoryText,
    hasUnansweredRadio,
  ]);

  const onChooseReason = (id: string) => {
    setSelectedReasonId(id);
    setAnswers({});
    setTextValidationErrors({});
    setShowCancelError(false);
  };

  const getLengthMaximum = (q: FollowUp) => {
    const lengthRule = (q.validationRules || []).find(
      (vr) => vr.type === "length"
    );
    const maxRaw = lengthRule?.metadata?.find((m) => m.key === "maximum")?.value;
    const maxNum = maxRaw !== undefined ? Number(maxRaw) : Number.NaN;
    return Number.isNaN(maxNum) ? undefined : maxNum;
  };

  const onText = (qid: string, v: string) => {
    setAnswers((s) => ({ ...s, [qid]: v }));
    setTextValidationErrors((prev) => {
      if (!prev[qid]) return prev;
      const next = { ...prev };
      delete next[qid];
      return next;
    });
    setShowCancelError(false);
  };

  const onRadio = (qid: string, optId: string) => {
    setAnswers((s) => ({ ...s, [qid]: optId }));
    setShowCancelError(false);
  };

  const buildRegexFromExpression = (raw?: string): RegExp | null => {
    if (!raw) return null;

    const s = raw.trim();
    const m = /^\/(.+)\/([a-z]*)$/i.exec(s);
    if (m) {
      const pattern = m[1];
      const flags = m[2] || "";
      try {
        return new RegExp(pattern, flags);
      } catch {
        return null;
      }
    }

    try {
      return new RegExp(s);
    } catch {
      return null;
    }
  };

  const runTextValidations = () => {
    if (!selectedReason) return true;
    const infos = (selectedReason.additionalInfo || []).filter(
      (q) => q?.isEnabled !== false && q.type === "Text"
    );
    const newErrors: Record<string, string[]> = {};
    infos.forEach((q) => {
      const value = (answers[q.questionId] || "").trim();
      const rules = q.validationRules || [];
      rules.forEach((rule) => {
        if (rule.type === "mandatory") {
          if (value === "") {
            if (!newErrors[q.questionId]) newErrors[q.questionId] = [];
            newErrors[q.questionId].push(rule.errorCode);
          }
        } else if (rule.type === "regex") {
          const expression =
            (rule.metadata || []).find((m) => m.key === "expression")?.value ||
            "";
          if (expression && value !== "") {
            const re = buildRegexFromExpression(expression);
            if (re && !re.test(value)) {
              if (!newErrors[q.questionId]) newErrors[q.questionId] = [];
              newErrors[q.questionId].push(rule.errorCode);
            }
          }
        } else if (rule.type === "length") {
          const maximumRaw = (rule.metadata || []).find(
            (m) => m.key === "maximum"
          )?.value;
          const maximum = maximumRaw ? Number(maximumRaw) : Number.NaN;
          if (!Number.isNaN(maximum) && value.length > maximum) {
            if (!newErrors[q.questionId]) newErrors[q.questionId] = [];
            newErrors[q.questionId].push(rule.errorCode);
          }
        }
      });
    });
    setTextValidationErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      const firstQid = Object.keys(newErrors)[0];
      const el = document.getElementById(`cr-q-${firstQid}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
    return Object.keys(newErrors).length === 0;
  };

  const waitForCancellationConfirmation = async (
    workOrderId: string | undefined
  ): Promise<boolean> => {
    if (!workOrderId) return true;

    const timeoutMs = (woCancellationTimeoutSeconds as number) * 1000;
    const intervalMs = (woCancellationPollingDelaySeconds as number) * 1000;

    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      try {
        const workOrderResponse = await api.getWorkOrderEntity(workOrderId);
        if (workOrderResponse?.status === "Cancelled") {
          return true;
        }
      } catch {}
      await sleep(intervalMs);
    }
    return false;
  };

  const handleCancelRepair = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!selectedReason) return;
    if (activeBlockingErrorCode) {
      if (blockingState.qid) {
        const el = document.getElementById(`cr-q-${blockingState.qid}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
      return;
    }

    const isValid = runTextValidations();
    if (!isValid) return;

    setShowCancelError(false);
    setIsCancelling(true);

    try {
      let comment: string | null = null;

      const infos = (selectedReason.additionalInfo || []).filter(
        (q) => q?.isEnabled !== false
      );

      infos.forEach((q) => {
        if (q.type === "Text") {
          const value = (answers[q.questionId] || "").trim();
          if (value) {
            comment = value;
          }
        } else if (q.type === "Radio") {
          const chosen = (answers[q.questionId] || "").trim();
          const opt = (q.options || []).find(
            (o) => String(o.id) === String(chosen)
          );
          if (opt?.value) {
            comment = opt.value;
          }
        }
      });

      const workOrderId = workOrderRepair?.worksOrderIdentifier;

      let assetTypeId: number | undefined = workOrderRepair?.premisesTypeId as
        | number
        | undefined;

      const areaOrTypeName = workOrderRepair?.area;

      if (
        !assetTypeId &&
        (areaOrTypeName === "Divided block" ||
          areaOrTypeName === "Divided Block")
      ) {
        assetTypeId = 5;
      }

      const repair = new UpdateRepair();
      const statuses = new Statuses();

      statuses.Status = "Cancelled";
      statuses.reasonCode = selectedReason.optionCode;
      statuses.comment = comment;

      repair.statuses = statuses;

      if (assetTypeId) {
        const assetType = new AssetType();
        const asset = new Asset();
        assetType.assetTypeId = assetTypeId;
        asset.type = assetType;
        repair.asset = asset;
      }

      await api.repairWorkOrderUpdate(repair, workOrderId);

      const confirmed = await waitForCancellationConfirmation(workOrderId);

      if (!confirmed) {
        throw new Error("Cancellation not confirmed");
      }

      props.history.replace(`/WorkOrder360View/${workOrderId}`);
    } catch (err) {
      setShowCancelError(true);
      setIsCancelling(false);
      return;
    }
    setIsCancelling(false);
  };

  const handleKeepRepair = (e: React.MouseEvent) => {
    e.preventDefault();
    props.history.push(
      `/WorkOrder360View/${workOrderRepair?.worksOrderIdentifier}`
    );
  };

  const handleBack = () => {
    props.history.push(
      `/WorkOrder360View/${workOrderRepair?.worksOrderIdentifier}`
    );
  };

  if (isLoading) {
    return (
      <div className="loader-panel">
        <LoadingSpinner />
      </div>
    );
  }

  if (isCancelling) {
    return (
      <div className="loader-panel">
        <div className="loader-text">
          {t("CancelRepair_InProgress_Text")}
        </div>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="container ctn-fht mx-auto pb-12 my-5" role="main">
      <div className="cr-inner">
        <button className="back-btn pl-0 mb-4 mt-15" onClick={handleBack}>
          <i className="fas fa-angle-left mr-2"></i>
          {t("back")}
        </button>

        <h1 className="cr-title mb-4">
          {t("CancelRepair_Header")}
        </h1>
        <p className="cr-sub">
          {t("CancelRepair_SubHeader")}
        </p>
        <p className="cr-why mb-4">{t("CancelRepair_Why")}</p>

        

        <fieldset className="cr-fieldset mb-4">
          {reasons.map((r) => (
            <div key={r.id} className="cr-radioRow">
              <label className="cr-radioLbl">
                <input
                  type="radio"
                  name="cancel-reason"
                  value={r.id}
                  checked={selectedReasonId === r.id}
                  onChange={() => onChooseReason(r.id)}
                />
                <span>{r.optionValue}</span>
              </label>

              {selectedReasonId === r.id &&
                (r.additionalInfo || []).filter((q) => q?.isEnabled !== false)
                  .length > 0 && (
                  <div className="cr-followUp">
                    {(r.additionalInfo || [])
                      .filter((q) => q?.isEnabled !== false)
                      .sort((a, b) => Number(a.sequence) - Number(b.sequence))
                      .map((q) =>
                        q.type === "Text" ? (
                          <div key={q.questionId} className="cr-inputWrap">
                            <label
                              className="cr-inputLbl"
                              htmlFor={`cr-q-${q.questionId}`}
                            >
                              {q.label}
                            </label>
                            <input
                              id={`cr-q-${q.questionId}`}
                              className="cr-input"
                              type="text"
                              value={answers[q.questionId] || ""}
                              onChange={(e) =>
                                onText(q.questionId, e.target.value)
                              }
                              size={Math.min(
                                80,
                                Math.max(
                                  6,
                                  Math.ceil((q.label.length + 2) * 0.8)
                                )
                              )}
                            />
                            {(textValidationErrors[q.questionId] || []).map(
                              (code) => {
                                const max = getLengthMaximum(q);

                                return (
                                  <div
                                    key={code}
                                    className="flex items-center w-full px-3 py-0 cursor-pointer bg-yellow mt-3"
                                  >
                                    <div className="pr-4 pl-2 pr-8">
                                      <i className="text-brick-100 w-6 h-6 fas fa-exclamation-triangle"></i>
                                    </div>
                                    <div className="w-full text-left my-2 font-AvantGardeGothic-Md">
                                      {t(code, { max })}
                                    </div>
                                  </div>
                                );
                              }
                            )}
                          </div>
                        ) : (
                          <div
                            key={q.questionId}
                            className="cr-radioGroup"
                            id={`cr-q-${q.questionId}`}
                          >
                            <span className="cr-inputLbl">{q.label}</span>
                            <div className="warning mb-8">
                              <div className="cr-ynRow">
                                {(q.options || []).map((opt) => (
                                  <label
                                    key={opt.id}
                                    className="cr-radioLbl cr-yn"
                                  >
                                    <input
                                      type="radio"
                                      name={q.questionId}
                                      value={opt.id}
                                      checked={
                                        (answers[q.questionId] || "") ===
                                        String(opt.id)
                                      }
                                      onChange={() =>
                                        onRadio(q.questionId, String(opt.id))
                                      }
                                    />
                                    <span>{opt.value}</span>
                                  </label>
                                ))}
                              </div>
                            </div>

                            {blockingState.qid === q.questionId && (
                              <div className="flex items-center w-full px-3 py-0 cursor-pointer bg-yellow">
                                <div className="pr-4 pl-2 pr-8">
                                  <i className="text-brick-100 w-6 h-6 fas fa-exclamation-triangle"></i>
                                </div>
                                <div className="w-full text-left my-2 font-AvantGardeGothic-Md">
                                  {t(activeBlockingErrorCode)}
                                </div>
                              </div>
                            )}
                          </div>
                        )
                      )}
                  </div>
                )}
            </div>
          ))}
        </fieldset>
        {showCancelError && (
          <div
            className="error mb-8 sm:mx-1"
            role="alert"
            id="cancel-failed-alert"
          >
            <div className="w-full px-2 py-2 bg-red-20 flex items-center">
              <div className="px-4 pr-8">
                <i className="fas fa-exclamation-circle me-2 text-brick-100 w-6 h-6 bolder-font"></i>
              </div>
              <div className="flex items-center">
                <p className="w-full text-left my-2 font-AvantGardeGothic-Md">
                  {t("CancelRepair_Failed_Alert")}
                </p>
              </div>
            </div>
          </div>
        )}
        <div className="cr-actions">
          <Button
            id="cr-cancel"
            className="button-class cr-btnSecondary"
            onClick={handleCancelRepair}
            disabled={!canSubmitCancel || isLoading || isCancelling}
          >
            <span>{t("CP077_CancelRepair")}</span>
            <i className="fas fa-angle-right relativetext-white pointer-events-none arrow"></i>
          </Button>
          <Button
            id="cr-keep"
            className="button-class cr-btnPrimary"
            onClick={handleKeepRepair}
            disabled={isLoading || isCancelling}
          >
            <span>{t("CancelRepair_Keep_Button")}</span>
            <i className="fas fa-angle-right relativetext-white pointer-events-none arrow"></i>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CancelRepair;